import React, {
	forwardRef,
	HTMLAttributes,
	ReactNode,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';
import styled from 'styled-components';
import { ImperativeCarouselItem } from './CarouselItem';
import gsap from 'gsap';
import { Draggable, ScrollTrigger, InertiaPlugin } from 'gsap/all';
import CarouselItem from './CarouselItem';

const Container = styled.div`
	min-width: 100%;
	width: max-content;
	display: grid;
	grid-auto-columns: 400px;
	grid-template-rows: max-content;
	position: relative;
`;

type CarouselProps = {
	loop?: boolean;
	onMove?: (index: number) => void;
	children: ReactNode[];
} & HTMLAttributes<HTMLDivElement>;

export type ImperativeCarousel = {
	move: (dir: 1 | -1) => void;
};

export default forwardRef<ImperativeCarousel, CarouselProps>(function CarouselContainer(
	{ loop, onMove, children, ...props },
	ref
) {
	const items = useRef<(ImperativeCarouselItem | null)[]>([]);
	const containerRef = useRef<HTMLDivElement>(null);

	const draggable = useRef<Draggable>(null);
	const proxy = useRef<HTMLDivElement>(null);

	const [activeIndex, setActiveIndex] = useState(0);
	const [viewbox, setViewbox] = useState({
		width: 0,
		offset: 0,
		parentWidth: 0,
	});
	const [gap, setGap] = useState(0);

	useImperativeHandle(ref, () => ({
		move: dir => {
			const activeItem = items.current[activeIndex];
			if (!activeItem) return;
			const width = activeItem.getBox().width + 40;
			const current = Number(gsap.getProperty(proxy.current, 'x'));
			const target = current - width * dir;
			gsap.to(proxy.current, {
				x: snapValue(target),
				duration: 0.4,
				onUpdate: () => {
					draggable.current?.update();
					handleUpdate(draggable.current?.x);
				},
			});
		},
	}));

	useEffect(() => {
		onMove?.(activeIndex);
	}, [activeIndex, onMove]);

	const handleResize = useCallback(() => {
		if (containerRef.current) {
			setViewbox({
				width: containerRef.current.clientWidth,
				offset: containerRef.current.offsetLeft,
				parentWidth: containerRef.current.parentElement?.clientWidth ?? 0,
			});
			setGap(Number(gsap.getProperty(containerRef.current, 'gap')));

			const current = Number(gsap.getProperty(proxy.current, 'x'));
			gsap.set(proxy.current, {
				x: snapValue(current),
			});
			draggable.current?.update();
			handleUpdate(draggable.current?.x);
		}
	}, [containerRef.current, items.current]);

	const handleUpdate = useCallback(
		(progress: number | undefined) => {
			if (progress == undefined) return;
			for (let i = 0; i < items.current.length; i++) {
				const item = items.current[i];
				if (!item) continue;

				const box = item.getBox();

				let pos = progress;
				if (loop) {
					const minPosition = box.min - viewbox.offset;
					const maxPosition = viewbox.width - box.max - viewbox.offset;
					pos = gsap.utils.wrap(minPosition, maxPosition, progress);
				}

				const minViewport = box.min + viewbox.offset / 2;
				const maxViewport = box.min - viewbox.offset / 1.5 + viewbox.parentWidth;

				item.setPos(pos);
				item.setActive(pos > minViewport && pos < maxViewport);
			}
		},
		[viewbox, items.current, loop]
	);

	const snapValue = useCallback(
		(value: number) => {
			if (!items.current) return value;
			let totalWidth = 0;
			const widths = items.current
				.map(item => {
					if (!item) return;
					const width = -item.getBox().width - gap;
					totalWidth += width;
					return totalWidth - width;
				})
				.filter(i => i !== undefined);

			if (!loop) {
				const snapped = gsap.utils.snap(widths, value);
				setActiveIndex(widths.indexOf(snapped));
				return snapped;
			}

			widths.push(totalWidth);
			const wrapped = gsap.utils.wrap(widths[0], totalWidth, value);
			const snapped = gsap.utils.snap(widths, wrapped);
			setActiveIndex(widths.indexOf(snapped) % items.current.length);
			return value - wrapped + snapped;
		},
		[gap, items.current, loop]
	);

	useEffect(() => {
		gsap.registerPlugin(ScrollTrigger, Draggable, InertiaPlugin);
		proxy.current = document.createElement('div');

		return () => {
			proxy.current?.remove();
		};
	}, []);

	useEffect(() => {
		handleResize();
		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [handleResize]);

	useEffect(() => {
		if (!containerRef.current || !proxy.current) {
			draggable.current?.kill();
			return;
		}

		draggable.current = Draggable.create(proxy.current, {
			trigger: containerRef.current,
			type: 'x',
			throwProps: true,
			throwResistance: 1,
			maxDuration: 0.25,
			allowEventDefault: true,
			allowContextMenu: true,
			snap: snapValue,
			onDrag: () => handleUpdate(draggable.current?.x),
			onThrowUpdate: () => handleUpdate(draggable.current?.x),
		})[0];

		draggable.current.update();
		handleUpdate(draggable.current.x);

		return () => {
			draggable.current?.kill();
		};
		// }, [snapValue]);
	}, [containerRef.current, proxy.current, handleUpdate, snapValue]);

	return (
		<Container ref={containerRef} {...props}>
			{children.map((child, index) => {
				return (
					<CarouselItem
						key={index}
						ref={ent => {
							items.current[index] = ent;
						}}
					>
						{child}
					</CarouselItem>
				);
			})}
		</Container>
	);
});
